import apisauce from 'apisauce';
import { Auth } from 'aws-amplify';

import { newCancelToken, stringify } from 'src/utils';
import appConfig from 'src/appConfig';
import {
  ChangePasswordPayload,
  ConfirmSignUpPayload,
  ForgotPasswordPayload,
  ResendSignUpPayload,
  SignInPayload,
  SignUpPayload,
  SubmitForgotPasswordPayload,
} from 'src/redux/auth/types';
import { TokenService } from '.';
import {
  AddDailyTimesheetsPayload,
  DeleteDailyTimesheetByIdPayload,
  GetTimesheetByIdPayload,
  GetTimesheetsParams,
  WeeklyTimesheetPayload,
} from 'src/redux/timesheet/types';
import { EmployeeFormType, GetEmployeesParams, GetVacantEmployeesParams } from 'src/redux/employees/types';
import { DeleteJobPayload, GetJobsParams, JobInfo } from 'src/redux/jobcodes/types';
import { CreateTaskPayload, DeleteTaskPayload, GetTasksParams, UpdateTaskPayload } from 'src/redux/task/types';
import { DeletePhasePayload, GetPhasesParams, PhaseInfo } from 'src/redux/phasecodes/types';
import { CreateUserPayload, GetUsersParams, UpdateUserPayload } from 'src/redux/users/types';

const create = (baseURL = appConfig.API_URL) => {
  //
  // Create and configure an apisauce-based api object.
  //

  const api = apisauce.create({
    baseURL,
    headers: {
      'Cache-Control': 'no-cache',
      Pragma: 'no-cache',
      Expires: 0,
      Accept: 'application/json',
    },
    timeout: appConfig.CONNECTION_TIMEOUT,
  });

  api.axiosInstance.interceptors.request.use(async config => {
    return TokenService.getToken()
      .then(token => {
        config.headers.Authorization = 'Bearer ' + token;
        return Promise.resolve(config);
      })
      .catch(() => {
        return Promise.resolve(config);
      });
  });

  api.axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      if (error.response.status === 401 && !originalRequest?._retry) {
        try {
          originalRequest._retry = true;
          TokenService.clearToken();
          const { data } = await exchangeToken();
          const token = (data as any).token;
          TokenService.setToken(token);

          originalRequest.headers['Authorization'] = 'Bearer ' + token;

          return api.axiosInstance(originalRequest);
        } catch (err) {
          return Promise.reject(error);
        }
      }
      return error;
    },
  );

  const getRoot = () => api.get('');

  // ====================== Auth ======================
  const exchangeToken = () => api.post('/sessions', {}, newCancelToken());
  const signIn = (body: SignInPayload) => Auth.signIn(body.username, body.password);
  const signUp = (body: SignUpPayload) => {
    const params = {
      username: body.username,
      password: body.password,
    };

    // const attributes = { phone_number: body.phoneNumber, given_name: body.firstName, family_name: body.lastName };
    const attributes = { email: body.username, given_name: body.firstName, family_name: body.lastName };

    return Auth.signUp({ ...params, attributes });
  };

  const resendSignUp = (body: ResendSignUpPayload) => Auth.resendSignUp(body.username);

  const confirmSignUp = (body: ConfirmSignUpPayload) => Auth.confirmSignUp(body.username, body.code);

  const signOut = () => Auth.signOut();

  const getPermission = () => api.get('/me/permission', {}, newCancelToken());
  const getAuthorizedUser = () => api.get('/users/authenticated', {}, newCancelToken());

  const forgotPassword = (body: ForgotPasswordPayload) => Auth.forgotPassword(body.email);

  const submitForgotPassword = (body: SubmitForgotPasswordPayload) =>
    Auth.forgotPasswordSubmit(body.email, body.token, body.password);

  const changePassword = (body: ChangePasswordPayload) =>
    Auth.changePassword(body.user, body.currentPassword, body.newPassword);

  // ====================== Timesheet ======================
  const getTimesheets = (params: GetTimesheetsParams) =>
    api.get(`/weekly-timesheets?${stringify(params)}`, {}, newCancelToken());

  const exportCSVTimesheets = (params: GetTimesheetsParams) =>
    api.get(`/weekly-timesheets/export-csv?${stringify(params)}`, {}, newCancelToken());

  const getDailyTimesheetsByWeekly = (params: GetTimesheetByIdPayload) =>
    api.get(`/weekly-timesheets/${params.id}/daily-timesheets`, {}, newCancelToken());

  const getWeeklyTimesheetsById = (params: GetTimesheetByIdPayload) =>
    api.get(`/weekly-timesheets/${params.id}`, {}, newCancelToken());

  const addWeeklyTimesheet = (body: WeeklyTimesheetPayload) => api.post(`/weekly-timesheets`, body, newCancelToken());
  const addDailyTimesheets = (body: AddDailyTimesheetsPayload) =>
    api.post(
      `/weekly-timesheets/${body.id}/daily-timesheets`,
      { dailyTimesheets: body.dailyTimesheets },
      newCancelToken(),
    );
  const getDailyTimesheetAudit = (uri: string) => api.get(uri, {}, newCancelToken());

  const deleteDailyTimesheetInWeekById = ({ weeklyTimesheetId, dailyTimesheetId }: DeleteDailyTimesheetByIdPayload) =>
    api.delete(`/weekly-timesheets/${weeklyTimesheetId}/daily-timesheets/${dailyTimesheetId}`, {}, newCancelToken());

  // ====================== Employees ======================
  const getEmployees = (params: GetEmployeesParams) => api.get(`/employees?${stringify(params)}`, {}, newCancelToken());

  const getVacantEmployees = (params: GetVacantEmployeesParams) => {
    const updatedParams = { ...params };
    delete updatedParams.callback;
    return api.get(`/employees/vacant-employees?${stringify(updatedParams)}`, {}, newCancelToken());
  };

  const updateEmployee = (body: EmployeeFormType) => api.put(`/employees/${body?.id}`, body, newCancelToken());
  const addEmployee = (body: EmployeeFormType) => api.post(`/employees`, body, newCancelToken());
  const exportCSVEmployees = () => api.get(`/employees/export-csv`, {}, newCancelToken());

  // ====================== JobCodes ======================
  const getJobs = (params: GetJobsParams) => {
    var queryString = { ...params };
    // Get All
    if (!queryString.take) {
      queryString.takeAll = 'true';
    }
    return api.get(`/job-codes?${stringify(queryString)}`, {}, newCancelToken());
  };
  const updateJob = (body: JobInfo) => api.put(`/job-codes/${body?.id}`, body, newCancelToken());
  const addJob = (body: JobInfo) => api.post(`/job-codes`, body, newCancelToken());
  const deleteJob = (data: DeleteJobPayload) => api.delete(`/job-codes/${data.id}`, {}, newCancelToken());

  // ====================== Tasks ======================
  const getTasks = (params: GetTasksParams) => {
    // Get All
    var queryString = { ...params };

    if (!queryString.take) {
      queryString.takeAll = 'true';
    }
    return api.get(`/tasks?${stringify(queryString)}`, {}, newCancelToken());
  };
  const updateTask = (data: UpdateTaskPayload) => api.put(`/tasks/${data.id}`, data.body, newCancelToken());
  const addTask = (body: CreateTaskPayload) => api.post(`/tasks`, body, newCancelToken());
  const deleteTask = (data: DeleteTaskPayload) => api.delete(`/tasks/${data.id}`, {}, newCancelToken());

  // ====================== PhaseCodes ======================
  const getPhases = (params: GetPhasesParams) => {
    // Get All
    var queryString = { ...params };
    if (!queryString.take) {
      queryString.takeAll = 'true';
    }
    return api.get(`/phase-codes?${stringify(queryString)}`, {}, newCancelToken());
  };
  const updatePhase = (body: PhaseInfo) => api.put(`/phase-codes/${body?.id}`, body, newCancelToken());
  const addPhase = (body: PhaseInfo) => api.post(`/phase-codes`, body, newCancelToken());
  const deletePhase = (data: DeletePhasePayload) => api.delete(`/phase-codes/${data.id}`, {}, newCancelToken());

  // ====================== Users ======================
  const getUsers = (params: GetUsersParams) => {
    const queryString = stringify(params);

    return api.get(`/users?${queryString}`, {}, newCancelToken());
  };
  const updateUser = (data: UpdateUserPayload) => api.put(`/users/${data.id}`, data.body, newCancelToken());
  const addUser = (body: CreateUserPayload) => api.post('/users', body, newCancelToken());

  // ====================== Options ======================

  const getRolesOption = () => api.get('/roles', {}, newCancelToken());
  const getLevelsOption = () => api.get('/levels', {}, newCancelToken());
  const getTradesOption = () => api.get('/trades', {}, newCancelToken());

  // Return back a collection of functions that we would consider our
  // interface.  Most of the time it'll be just the list of all the
  //
  // Notice we're not returning back the `api` created in step 1. That's
  // because it is scoped privately.  This is one way to create truly
  // private scoped goodies in JavaScript.
  //
  return {
    getRoot,
    // ====================== Auth ======================
    exchangeToken,
    getPermission,
    getAuthorizedUser,
    signIn,
    signUp,
    resendSignUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    submitForgotPassword,
    changePassword,

    // ====================== Timesheet ======================
    getTimesheets,
    exportCSVTimesheets,
    getDailyTimesheetsByWeekly,
    getWeeklyTimesheetsById,
    addWeeklyTimesheet,
    addDailyTimesheets,
    getDailyTimesheetAudit,
    deleteDailyTimesheetInWeekById,
    // ====================== Employees ======================

    getEmployees,
    getVacantEmployees,
    updateEmployee,
    addEmployee,
    exportCSVEmployees,

    // ====================== JobCodes ======================
    getJobs,
    updateJob,
    addJob,
    deleteJob,

    // ====================== Tasks ======================
    getTasks,
    updateTask,
    addTask,
    deleteTask,

    // ====================== PhaseCodes ======================
    getPhases,
    updatePhase,
    addPhase,
    deletePhase,

    // ====================== User ======================
    getUsers,
    updateUser,
    addUser,

    // ====================== Options ======================
    getRolesOption,
    getLevelsOption,
    getTradesOption,
  };
};

export type Apis = ReturnType<typeof create>;

export default {
  create,
};
